import { SxProps, Theme } from '@mui/material/styles';
import TextField, { OutlinedTextFieldProps } from '@mui/material/TextField';

import { COLORS } from '@/styles/tokens/colors';

import { FormControl } from '../FormControl';
import {
  BaseTextInputProps,
  FormControlTextInputProps,
  HelpTextVariant,
} from '../inputTypes';

type MUITextInputProps = Omit<
  OutlinedTextFieldProps,
  // these conflict with our internally-defined props
  'maxRows' | 'rows' | 'onKeyDown' | 'onKeyUp' | 'variant'
>;

interface TextInputProps extends FormControlTextInputProps, MUITextInputProps {
  label: React.ReactNode;
  hideLabel?: boolean;
  id?: string;
  // the presence of contextualHelp indicates that there's help text related to this input, which
  // will cause the "info" icon to show up next to the input label
  contextualHelp?: JSX.Element;
  errorMessage?: string;
  helpText?: string;
  helpTextVariant?: HelpTextVariant;
  labelIconEnd?: React.ReactNode;
  autoFocus?: boolean;
  resizable?: boolean; // Add this prop to enable resize handles
}

// Layout glue that makes a multiline TextField stretch to fill its parent's
// vertical space. Applied via sx so callers only need to set `fillHeight` —
// height: 100% doesn't resolve reliably for flex children in a column
// container, hence the flex: 1 / minHeight: 0 chain. `height: auto !important`
// is required to defeat TextareaAutosize's inline height style.
const FILL_HEIGHT_FIELD_SX: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  '& .MuiInputBase-root': {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'stretch',
  },
  '& .MuiInputBase-input.MuiInputBase-inputMultiline': {
    flex: 1,
    minHeight: 0,
    height: 'auto !important',
    overflow: 'auto !important',
    resize: 'none !important',
  },
};

export function BaseTextInput({
  // see here: https://github.com/mui/material-ui/issues/12946
  // stripping off onKeyUp and onKeyDown is required here because TextField doesn't actually implement those props.
  onKeyUp: _onKeyUp,
  onKeyDown: _onKeyDown,
  startAdornment,
  endAdornment,
  testId,
  autoFocus,
  onKeyDown,
  resizable,
  fillHeight,
  sx,
  ...otherProps
}: BaseTextInputProps & MUITextInputProps) {
  const { disabled, multiline } = otherProps;
  const disabledStyle = disabled ? { background: COLORS.GRAY[50] } : {};
  const multilineStyle: Record<string, unknown> = {};
  if ((otherProps.rows && otherProps.rows > 1) || multiline) {
    multilineStyle.padding = 0;
  }

  // Add resize style if resizable prop is set and it's a multiline input
  const resizeStyle: Record<string, unknown> = {};
  if (resizable && multiline) {
    resizeStyle.resize = 'vertical';
  }

  const mergedSx = Object.assign(
    { background: 'white' },
    disabledStyle,
    multilineStyle,
    resizeStyle
  );

  return (
    <TextField
      variant="outlined"
      {...otherProps}
      sx={
        fillHeight
          ? ([
              FILL_HEIGHT_FIELD_SX,
              ...(Array.isArray(sx) ? sx : [sx]),
            ] as SxProps<Theme>)
          : sx
      }
      InputProps={{
        sx: mergedSx,
        startAdornment,
        endAdornment,
        autoFocus,
        onKeyDown,
        ...otherProps.InputProps,
      }}
      inputProps={{
        ...otherProps.inputProps,
        'data-testid': testId ?? `text-input-${otherProps.name ?? ''}`,
        style: {
          ...(otherProps.inputProps?.style || {}),
          ...(resizable && multiline ? { resize: 'vertical' } : {}),
        },
      }}
    />
  );
}

// Forwarded to FormControl's outer Stack so the input participates in the
// parent flex column when fillHeight is set.
const FILL_HEIGHT_CONTAINER_SX = { flex: 1, minHeight: 0 } as const;

export function TextInput({
  label,
  hideLabel,
  contextualHelp,
  errorMessage,
  helpText,
  helpTextVariant,
  labelIconEnd,
  fillHeight,
  ...inputProps
}: TextInputProps) {
  return (
    <FormControl<BaseTextInputProps>
      component={BaseTextInput}
      inputProps={{ ...inputProps, fillHeight }}
      errorMessage={errorMessage}
      contextualHelp={contextualHelp}
      required={inputProps.required}
      helpText={helpText}
      helpTextVariant={helpTextVariant}
      id={inputProps.id}
      label={label}
      hideLabel={hideLabel}
      labelIconEnd={labelIconEnd}
      containerSx={fillHeight ? FILL_HEIGHT_CONTAINER_SX : undefined}
    />
  );
}
